import { getAuthentication } from "../../lib/auth";
import { TOP10_ARTICLE_URL } from "../../lib/env";
import { escapeGqlString, gqlRequest } from "../../lib/functions";
import {
  ARTICLERICHTEXTGQLSCHEMA,
  TOP10ARTICLEGQLSCHEMA,
} from "../../lib/gql/Top10Article";
import { IMultiLanguageInputArgs } from "../../types/MultiLanguageInput";
import { ITop10Article } from "../../types/Top10Article";
import { ITop10ArticleRichtText } from "../../types/Top10Article.RichText";

export async function getTop10Articles(
  limit: number,
  offset: number,
  query: string
): Promise<ITop10Article[]> {
  let filters = "";
  if (query && query.trim() !== "") {
    filters = `,query:"${query}"`;
  }
  const gqlString = `query{
        GetTop10Articles(limit:${limit},offset:${offset}${filters}){
            ${TOP10ARTICLEGQLSCHEMA}
        }
    }`;
  try {
    const response = await gqlRequest<{
      GetTop10Articles: ITop10Article[];
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.GetTop10Articles || [];
  } catch (err) {
    throw err;
  }
}

export async function getTop10ArticleByID(
  id: string
): Promise<ITop10Article | null> {
  const gqlString = `query{
        GetTop10ArticleByID(id:"${id}"){
            ${TOP10ARTICLEGQLSCHEMA}
        }
    }`;

  try {
    const response = await gqlRequest<{
      GetTop10ArticleByID: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response?.data?.GetTop10ArticleByID || null;
  } catch (err) {
    throw err;
  }
}

export async function addNewTop10Article(
  title: IMultiLanguageInputArgs
): Promise<ITop10Article | null | undefined> {
  const gqlString = `mutation{
        AddNewTop10Article(title:{
            en:"${escapeGqlString(title.en)}",
            ar:"${escapeGqlString(title.ar)}",
            fr:"${escapeGqlString(title.fr)}"
        }){
            ${TOP10ARTICLEGQLSCHEMA}    
        }
    }`;

  try {
    const response = await gqlRequest<{
      AddNewTop10Article: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.AddNewTop10Article || null;
  } catch (err) {
    throw err;
  }
}

export async function editTop10ArticleTitle(
  id: string,
  title: IMultiLanguageInputArgs
): Promise<ITop10Article | null | undefined> {
  const gqlString = `mutation{
        EditTop10ArticleTitle(id:"${id}",title:{
            en:"${escapeGqlString(title.en)}",
            ar:"${escapeGqlString(title.ar)}",
            fr:"${escapeGqlString(title.fr)}"
        }){
            ${TOP10ARTICLEGQLSCHEMA}    
        }
    }`;

  try {
    const response = await gqlRequest<{
      EditTop10ArticleTitle: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditTop10ArticleTitle || null;
  } catch (err) {
    throw err;
  }
}

export async function editTop10ArticleDescription(
  id: string,
  description: IMultiLanguageInputArgs
): Promise<ITop10Article | null | undefined> {
  const gqlString = `mutation{
        EditTop10ArticleDescription(id:"${id}",description:{
            en:"${escapeGqlString(description.en)}",
            ar:"${escapeGqlString(description.ar)}",
            fr:"${escapeGqlString(description.fr)}"
        }){
            ${TOP10ARTICLEGQLSCHEMA}    
        }
    }`;

  try {
    const response = await gqlRequest<{
      EditTop10ArticleDescription: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditTop10ArticleDescription || null;
  } catch (err) {
    throw err;
  }
}

export async function pushKeywordIntoTop10Article(
  id: string,
  keyword: IMultiLanguageInputArgs
): Promise<ITop10Article | undefined | null> {
  const gqlString = `mutation{
        PushKeywordIntoTop10Article(id:"${id}",keyword:{
            en:"${escapeGqlString(keyword.en)}",
            ar:"${escapeGqlString(keyword.ar)}",
            fr:"${escapeGqlString(keyword.fr)}"
        }){
            ${TOP10ARTICLEGQLSCHEMA}    
        }
    }`;

  try {
    const response = await gqlRequest<{
      PushKeywordIntoTop10Article: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PushKeywordIntoTop10Article || null;
  } catch (err) {
    throw err;
  }
}

export async function pullKeywordFromTop10Article(
  id: string,
  keyword: IMultiLanguageInputArgs
): Promise<ITop10Article | null | undefined> {
  const gqlString = `mutation{
        PullKeywordFromTop10Article(id:"${id}",keyword:{
            en:"${escapeGqlString(keyword.en)}",
            ar:"${escapeGqlString(keyword.ar)}",
            fr:"${escapeGqlString(keyword.fr)}"
        }){
            ${TOP10ARTICLEGQLSCHEMA}    
        }
    }`;

  try {
    const response = await gqlRequest<{
      PullKeywordFromTop10Article: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response?.data?.PullKeywordFromTop10Article || null;
  } catch (err) {
    throw err;
  }
}

export async function updateTop10ArticleImage(
  id: string,
  image: string
): Promise<ITop10Article | undefined | null> {
  const gqlString = `mutation{
        UpdateTop10ArticleImage(id:"${id}",image:"${image}"){
            ${TOP10ARTICLEGQLSCHEMA}
        }
    }`;

  try {
    const response = await gqlRequest<{
      UpdateTop10ArticleImage: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.UpdateTop10ArticleImage || null;
  } catch (err) {
    throw err;
  }
}

export async function setTop10BettingWebsites(
  id: string,
  websites: string[]
): Promise<ITop10Article | undefined | null> {
  const websitesString = websites.map((w) => `"${w}"`).join(",");

  const gqlString = `mutation {
      SetTop10BettingWebsites(id: "${id}", websites: [${websitesString}]) {
        ${TOP10ARTICLEGQLSCHEMA}
      }
    }`;

  try {
    const response = await gqlRequest<{
      SetTop10BettingWebsites: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response?.data?.SetTop10BettingWebsites || null;
  } catch (err) {
    throw err;
  }
}

export async function addRichTextToArticle(
  id: string,
  title: IMultiLanguageInputArgs,
  content: IMultiLanguageInputArgs
): Promise<ITop10Article | null | undefined> {
  const gqlString = `mutation{
        AddNewRichTextArticle(id:"${id}",
            title:{
                en:"${escapeGqlString(title.en)}",
                ar:"${escapeGqlString(title.ar)}",
                fr:"${escapeGqlString(title.fr)}"
            },
             content:{
                en:"${escapeGqlString(content.en)}",
                ar:"${escapeGqlString(content.ar)}",
                fr:"${escapeGqlString(content.fr)}"
            }
        ){
            ${TOP10ARTICLEGQLSCHEMA}    
        }
    }`;

  try {
    const response = await gqlRequest<{
      AddNewRichTextArticle: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.AddNewRichTextArticle || null;
  } catch (err) {
    throw err;
  }
}

export async function deleteRichTextFromArticle(
  id: string,
  richTextId: string
): Promise<ITop10Article | null | undefined> {
  const gqlString = `mutation{
        DeleteRichTextFromArticle(id:"${id}",richTextId:"${richTextId}"){
            ${TOP10ARTICLEGQLSCHEMA}
        }
    }`;

  try {
    const response = await gqlRequest<{
      DeleteRichTextFromArticle: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.DeleteRichTextFromArticle || null;
  } catch (err) {
    throw err;
  }
}

export async function sortArticleSortedItems(
  id: string,
  newOrder: { Type: "TOP10" | "TEXT"; RichText?: string }[]
): Promise<ITop10Article | undefined | null> {
  const newOrderString = newOrder
    .map((item) => {
      // Only include RichText if it exists
      const richTextPart = item.RichText
        ? `, RichText: "${item.RichText}"`
        : "";
      return `{ Type: ${item.Type}${richTextPart} }`;
    })
    .join(", ");

  const gqlString = `mutation {
      SortArticleSortedItems(id: "${id}", newOrder: [${newOrderString}]) {
        ${TOP10ARTICLEGQLSCHEMA}
      }
    }`;

  try {
    const response = await gqlRequest<{
      SortArticleSortedItems: ITop10Article;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response?.data?.SortArticleSortedItems || null;
  } catch (err) {
    throw err;
  }
}

export async function deleteTop10Article(
  id: string
): Promise<boolean | undefined> {
  const gqlString = `mutation{
        DeleteTop10Article(id:"${id}")
    }`;

  try {
    const response = await gqlRequest<{
      DeleteTop10Article: boolean;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response?.data?.DeleteTop10Article;
  } catch (err) {
    throw err;
  }
}

export async function editRichTextByID(
  id: string,
  title: IMultiLanguageInputArgs,
  content: IMultiLanguageInputArgs
): Promise<ITop10ArticleRichtText | null> {
  const gqlString = `mutation{
     EditRichTextByID(id:"${id}",
            title:{
                en:"${escapeGqlString(title.en)}",
                ar:"${escapeGqlString(title.ar)}",
                fr:"${escapeGqlString(title.fr)}"
            },
             content:{
                en:"${escapeGqlString(content.en)}",
                ar:"${escapeGqlString(content.ar)}",
                fr:"${escapeGqlString(content.fr)}"
            }
        ){
            ${ARTICLERICHTEXTGQLSCHEMA}    
        }
  }`;

  try {
    const response = await gqlRequest<{
      EditRichTextByID: ITop10ArticleRichtText;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditRichTextByID || null;
  } catch (err) {
    throw err;
  }
}

export async function getRichTextByID(
  id: string
): Promise<ITop10ArticleRichtText | null> {
  const gqlString = `query{
    GetRichTextByID(id:"${id}"){
            ${ARTICLERICHTEXTGQLSCHEMA}    
    }
  }`;
  try {
    const response = await gqlRequest<{
      GetRichTextByID: ITop10ArticleRichtText;
    }>({
      query: gqlString,
      endpoint: TOP10_ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.GetRichTextByID || null;
  } catch (err) {
    throw err;
  }
}
